import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from './ui/badge';
import { Calendar, Trash2, AudioLines, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { memoryHasAudio } from '../lib/memoryMedia';
import { Memory } from '../services/api';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

interface MemoryGridViewProps {
  memories: Memory[];
  searchQuery: string;
  onMemoryClick: (memoryId: string) => void;
  onMemoryDelete: (memoryId: string) => void;
  highlightText: (text: string, query: string) => React.ReactNode;
}

// Dynamic color generation based on category name
const generateCategoryColor = (category: string) => {
  // Simple hash function to generate consistent colors from category names
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    const char = category.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // Generate HSL values for consistent, pleasing colors
  const hue = Math.abs(hash) % 360;
  const saturation = 65 + (Math.abs(hash) % 20); // 65-85%
  const lightness = 45 + (Math.abs(hash) % 15); // 45-60%
  
  return {
    hue,
    saturation,
    lightness,
    // CSS custom properties for dynamic styling
    cssVars: {
      '--category-hue': hue.toString(),
      '--category-sat': `${saturation}%`,
      '--category-light': `${lightness}%`,
    }
  };
};

const MemorySquare: React.FC<{
  memory: Memory;
  index: number;
  searchQuery: string;
  onMemoryClick: (memoryId: string) => void;
  onMemoryDelete: (memoryId: string) => void;
  highlightText: (text: string, query: string) => React.ReactNode;
}> = ({ memory, index, searchQuery, onMemoryClick, onMemoryDelete, highlightText }) => {
  const { t } = useTranslation('memories');
  const [isHovered, setIsHovered] = useState(false);
  
  // Generate dynamic colors based on category
  const category = (memory as any).category || 'general';
  const categoryColor = generateCategoryColor(category);
  
  // Calculate importance-based border thickness
  const importance = (memory as any).importance || 5;
  const borderThickness = Math.max(1, Math.floor(importance / 3)); // 1-3px based on importance 1-10

  const truncateText = (text: string, maxLength: number = 100) => {
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.div
            layout
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ 
              duration: 0.3, 
              delay: index * 0.02,
              type: 'spring',
              damping: 25,
              stiffness: 300
            }}
            className="group relative"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <motion.div
              className="relative w-16 h-16 rounded-lg backdrop-blur-md cursor-pointer transition-all duration-200 flex items-center justify-center overflow-hidden"
              style={{
                ...categoryColor.cssVars,
                backgroundColor: `hsl(var(--category-hue), var(--category-sat), calc(var(--category-light) + 35%), 0.15)`,
                borderColor: `hsl(var(--category-hue), var(--category-sat), var(--category-light), 0.4)`,
                borderWidth: `${borderThickness}px`,
                borderStyle: 'solid',
                boxShadow: isHovered 
                  ? `0 4px 20px hsl(var(--category-hue), var(--category-sat), var(--category-light), 0.3)`
                  : 'none'
              }}
              whileHover={{ 
                scale: 1.1,
                transition: { duration: 0.15 }
              }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onMemoryClick(memory.id)}
            >
              {/* Content preview - first letter or icon with improved contrast */}
              <div 
                className="text-xs font-semibold transition-all duration-200"
                style={{
                  color: isHovered 
                    ? `hsl(var(--category-hue), var(--category-sat), 15%)` // Dark text on hover
                    : `hsl(var(--category-hue), var(--category-sat), 25%)`, // Slightly lighter when not hovered
                  opacity: isHovered ? 1 : 0.8
                }}
              >
                {memory.content.charAt(0).toUpperCase()}
              </div>
              
              {/* Importance indicator - subtle dots */}
              <div className="absolute bottom-1 right-1 flex space-x-0.5">
                {Array.from({ length: Math.min(importance, 5) }).map((_, i) => (
                  <div
                    key={i}
                    className="w-1 h-1 rounded-full"
                    style={{
                      backgroundColor: `hsl(var(--category-hue), var(--category-sat), 20%)`,
                      opacity: 0.6
                    }}
                  />
                ))}
              </div>

              {/* Tags count or audio indicator */}
              <div className="absolute top-1 left-1 flex items-center gap-0.5">
                {memoryHasAudio(memory) && (
                  <span
                    className="flex h-2 w-2 items-center justify-center rounded-full bg-orange-400/90"
                    title={t('audio.badgeShort')}
                    aria-label={t('audio.badgeShort')}
                  >
                    <AudioLines className="h-1.5 w-1.5 text-white" aria-hidden />
                  </span>
                )}
                {memory.tags.length > 0 && (
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: `hsl(var(--category-hue), var(--category-sat), 35%)`,
                      opacity: 0.7,
                    }}
                  />
                )}
              </div>

              {/* Delete button - appears on hover */}
              <AnimatePresence>
                {isHovered && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg z-10"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMemoryDelete(memory.id);
                    }}
                  >
                    <Trash2 className="w-2.5 h-2.5" />
                  </motion.button>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </TooltipTrigger>
        
        <TooltipContent 
          side="top" 
          className="max-w-xs bg-gray-900/95 backdrop-blur-lg border border-gray-700/50 shadow-xl z-50"
        >
          <div className="space-y-2">
            {/* Memory content preview */}
            <div className="text-sm text-gray-100">
              {highlightText(truncateText(memory.content), searchQuery)}
            </div>
            
            {/* Tags */}
            {memory.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {memory.tags.slice(0, 4).map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="text-xs px-1.5 py-0.5 bg-gray-700/70 text-gray-200 border-gray-600/50"
                  >
                    #{tag}
                  </Badge>
                ))}
                {memory.tags.length > 4 && (
                  <span className="text-xs text-gray-400">
                    +{memory.tags.length - 4} more
                  </span>
                )}
              </div>
            )}
            
            {/* Metadata */}
            <div className="flex items-center justify-between text-xs text-gray-400">
              <div className="flex items-center space-x-1">
                <Calendar className="w-3 h-3" />
                <span>{new Date(memory.created_at).toLocaleDateString()}</span>
              </div>
              {importance && (
                <div className="flex items-center space-x-1">
                  <span>⭐ {importance}/10</span>
                </div>
              )}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

function MemoryGridView({
  memories,
  searchQuery,
  onMemoryClick,
  onMemoryDelete,
  highlightText
}: MemoryGridViewProps) {
  const gridContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.02,
        delayChildren: 0.1
      }
    }
  };

  return (
    <motion.div
      variants={gridContainer}
      initial="hidden"
      animate="show"
      className="w-full"
    >
      {memories.length === 0 ? (
        <div className="text-center py-12 text-workspace-text-secondary">
          <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No memories to display in grid view</p>
        </div>
      ) : (
        <div className="grid grid-cols-5 sm:grid-cols-8 lg:grid-cols-10 gap-2 sm:gap-3 lg:gap-4 auto-rows-min">
          {/* Responsive grid: 5 on mobile, 8 on tablet, 10 on desktop */}
          
          {memories.map((memory, index) => (
            <MemorySquare
              key={memory.id}
              memory={memory}
              index={index}
              searchQuery={searchQuery}
              onMemoryClick={onMemoryClick}
              onMemoryDelete={onMemoryDelete}
              highlightText={highlightText}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}

// Memoize component to prevent unnecessary re-renders
export default React.memo(MemoryGridView, (prevProps, nextProps) => {
  // Only re-render if memories array reference changes or other props change
  return (
    prevProps.memories === nextProps.memories &&
    prevProps.searchQuery === nextProps.searchQuery &&
    prevProps.onMemoryClick === nextProps.onMemoryClick &&
    prevProps.onMemoryDelete === nextProps.onMemoryDelete &&
    prevProps.highlightText === nextProps.highlightText
  );
}); 